// Replace Heroicons with Lucide
import { 
  Users, // Replaces UserGroupIcon
  FileText, // Replaces DocumentTextIcon
  Clock, // Replaces ClockIcon
  Percent, // Added for Percentage Rate
} from 'lucide-react';
// Import hooks and CountUp
import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
// Import cn utility
import { cn } from "@/lib/utils";

// Updated stats data with Lucide icons and refined colors/labels
const stats = [
  {
    id: 'accuracy',
    label: 'Matching Accuracy', // Refined label
    value: 95,
    suffix: '%',
    icon: Percent,
    iconColor: 'text-blue-500', 
    bgColor: 'bg-blue-100/60',
  },
  {
    id: 'time',
    label: 'Avg. Time Saved / Hire', // Refined label
    value: 75,
    suffix: '%',
    icon: Clock,
    iconColor: 'text-indigo-500',
    bgColor: 'bg-indigo-100/60',
  },
  {
    id: 'cvs',
    label: 'CVs Processed',
    value: 100000,
    suffix: '+',
    icon: FileText,
    iconColor: 'text-purple-500',
    bgColor: 'bg-purple-100/60',
  },
  {
    id: 'recruiters',
    label: 'Recruiters Empowered',
    value: 5000,
    suffix: '+',
    icon: Users,
    iconColor: 'text-pink-500',
    bgColor: 'bg-pink-100/60',
  }
];

// Refined AnimatedStat component using theme colors
function AnimatedStat({ stat }: { stat: typeof stats[0] }) {
  const [startCounting, setStartCounting] = useState(false);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3, // Trigger slightly earlier
  });

  useEffect(() => {
    if (inView) {
      setStartCounting(true);
    }
  }, [inView]);

  return (
    // Add subtle background/border on hover for the whole stat block
    <div 
        ref={ref} 
        className="flex flex-col items-center text-center p-6 rounded-lg group transition-colors duration-300 hover:bg-muted/50"
    >
      {/* Enhanced Icon Styling */}
      <div className={cn(
          "mb-4 flex h-12 w-12 items-center justify-center rounded-full shadow-sm",
          stat.bgColor,
          "group-hover:scale-110 group-hover:shadow-md transition-transform duration-300"
         )}>
        <stat.icon className={cn("h-6 w-6", stat.iconColor)} aria-hidden="true" />
      </div>
      {/* Stat Value with CountUp */}
      <dd className={cn(
          "order-first text-4xl sm:text-5xl font-bold tracking-tight", 
          stat.iconColor // Use icon color for the number for consistency
         )}>
        {startCounting ? (
          <CountUp 
             end={stat.value} 
             duration={2.5} 
             separator="," 
             suffix={stat.suffix}
             // prefix={stat.prefix} // Prefix handled outside if needed
          />
        ) : (
          `0${stat.suffix ?? ''}` // Show starting state with suffix
        )}
      </dd>
      {/* Stat Label */}
      <dt className="mt-2 text-sm font-medium text-muted-foreground">
        {stat.label}
      </dt>
    </div>
  );
}

export function StatsSection() {
  return (
    // Use theme background, adjust padding
    <div className="bg-muted/40 py-20 sm:py-28"> {/* Use muted background */} 
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
         {/* Use consistent Section Header styling */}
         <div className="text-center mb-16 sm:mb-20">
           <h2 className={cn(
              "text-sm font-semibold tracking-wide uppercase mb-2",
              "text-primary"
          )}>
            Proven Results
          </h2>
          <p className="mt-1 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground">
            Trusted by Teams Worldwide
          </p>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground mx-auto">
            Our platform delivers measurable efficiency and accuracy gains.
          </p>
        </div>

        {/* Stats Grid - Adjusted grid gap */}
        <dl className="grid grid-cols-1 gap-y-10 gap-x-8 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <AnimatedStat key={stat.id} stat={stat} />
          ))}
        </dl>
      </div>
    </div>
  );
}